import type { MachineHarnessAuthView } from '@nightshift/ui-types';

/** How one harness chip on the Agents view reads: its tone, a short suffix, and the hover text. */
export interface HarnessLoginChip {
  tone: 'ok' | 'failed' | 'unknown' | 'unreported';
  suffix: string;
  title: string;
}

function ago(at: number, now: number): string {
  const minutes = Math.max(0, Math.round((now - at) / 60_000));
  if (minutes < 1) return 'just now';
  if (minutes < 90) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  return hours < 48 ? `${hours} h ago` : `${Math.round(hours / 24)} days ago`;
}

/**
 * The chip for one installed harness. A lost login says so on the chip itself, because it is
 * the one state that changes where work goes; the probe's own words and the time sit in the
 * hover text. Unknown means the CLI has no status command (Kimi) or its answer was unreadable.
 */
export function harnessLoginChip(harness: string, entry: MachineHarnessAuthView | undefined, now: number): HarnessLoginChip {
  if (!entry) {
    return { tone: 'unreported', suffix: '', title: `${harness}: no login report yet (runner older than this check, or its first probe is still running)` };
  }
  const when = ago(entry.checkedAt, now);
  const detail = entry.detail ? ` — ${entry.detail}` : '';
  if (entry.state === 'failed') {
    const hold = entry.holdUntil !== null && entry.holdUntil > now
      ? ` Held until at least ${new Date(entry.holdUntil).toLocaleTimeString()}, because an attempt failed to authenticate.`
      : '';
    return {
      tone: 'failed',
      suffix: 'login lost',
      title: `${harness}: login lost (${entry.source === 'attempt' ? 'an attempt failed to authenticate' : 'the runner\'s probe'}, ${when})${detail}. Not offered work until the probe says it is back.${hold}`,
    };
  }
  if (entry.state === 'unknown') {
    return { tone: 'unknown', suffix: '?', title: `${harness}: login state unknown (${when})${detail}. Still offered work.` };
  }
  return { tone: 'ok', suffix: '', title: `${harness}: signed in (${when})${detail}` };
}
